import {debounce, getConfig, once} from './core.js';
import {requestMultiCartDialog} from './cart.js';

const PRODUCT_LIMIT = 6;
const CATEGORY_LIMIT = 4;
const HIGHLIGHT_TAGS = new Set(['SR1', 'SR2']);

function interpolate(template, values) {
	return Object.entries(values).reduce(
		(result, [key, value]) => result.split(`%${key}%`).join(String(value)),
		String(template || ''),
	);
}

function createIcon(name, className = '') {
	const icon = document.createElement('i');
	icon.className = `bi bi-${name}${className ? ` ${className}` : ''}`;
	icon.setAttribute('aria-hidden', 'true');
	return icon;
}

function createHighlightedContent(html, fallback) {
	const parsed = new DOMParser().parseFromString(String(html || fallback || ''), 'text/html');
	const fragment = document.createDocumentFragment();

	const appendNode = (node, target, highlighted = false, kind = '') => {
		if (node.nodeType === Node.TEXT_NODE) {
			if (!highlighted) {
				target.append(document.createTextNode(node.textContent || ''));
				return;
			}
			const mark = document.createElement('mark');
			mark.dataset.highlightKind = kind.toLowerCase();
			mark.textContent = node.textContent || '';
			target.append(mark);
			return;
		}
		if (node.nodeType !== Node.ELEMENT_NODE) {
			return;
		}
		const isHighlight = HIGHLIGHT_TAGS.has(node.tagName);
		node.childNodes.forEach((child) => appendNode(
			child,
			target,
			isHighlight || highlighted,
			isHighlight ? node.tagName : kind,
		));
	};

	parsed.body.childNodes.forEach((node) => appendNode(node, fragment));
	if ((fragment.textContent || '').trim() === '') {
		fragment.append(document.createTextNode(String(fallback || '')));
	}
	return fragment;
}

function suggestionUrl(url, term) {
	const target = new URL(String(url || ''), window.location.origin);
	if (term) {
		target.searchParams.set('suggest', term);
	}
	return `${target.pathname}${target.search}${target.hash}`;
}

function cartDialogUrl(karta) {
	const target = new URL(window.location.href);
	target.hash = '';
	target.search = '';
	target.searchParams.set('karta', String(karta));
	target.searchParams.set('do', 'dlgAddToCart');
	return `${target.pathname}${target.search}`;
}

function variantLabel(type, messages) {
	if (type === 'V') {
		return messages.variants;
	}
	if (type === 'B' || type === 'P') {
		return messages.colors;
	}
	return messages.colorsVariants;
}

function createImage(item) {
	const wrapper = document.createElement('span');
	wrapper.className = 'autocomplete-result__image';
	wrapper.append(createIcon('image'));
	if (!item.imageUrl) {
		return wrapper;
	}

	const image = document.createElement('img');
	image.src = item.imageUrl;
	image.alt = '';
	image.width = 56;
	image.height = 56;
	image.decoding = 'async';
	image.addEventListener('load', () => wrapper.classList.add('has-image'));
	image.addEventListener('error', () => image.remove());
	wrapper.append(image);
	return wrapper;
}

function createProduct(item, term, config) {
	const messages = config.autocomplete || {};
	const row = document.createElement('div');
	row.className = 'autocomplete-result autocomplete-result--product';

	const link = document.createElement('a');
	link.className = 'autocomplete-result__main autocomplete-primary-action';
	link.href = suggestionUrl(item.url, term);
	link.append(createImage(item));

	const content = document.createElement('span');
	content.className = 'autocomplete-result__content';
	const title = document.createElement('span');
	title.className = 'autocomplete-result__title';
	title.append(createHighlightedContent(item.h, item.label));
	content.append(title);

	const meta = document.createElement('span');
	meta.className = 'autocomplete-result__meta';
	if (item.karta) {
		const card = document.createElement('span');
		card.className = 'autocomplete-result__card';
		card.textContent = `${messages.card || ''} ${item.karta}`.trim();
		meta.append(card);
	}
	if (Number.isFinite(Number(item.totalCount))) {
		const count = document.createElement('span');
		count.className = 'autocomplete-result__count';
		count.textContent = `${Number(item.availableCount) || 0} ${messages.available || ''} / ${Number(item.totalCount) || 0} ${messages.total || ''} ${variantLabel(item.variantType, messages)}`.trim();
		meta.append(count);
	}
	content.append(meta);
	link.append(content);
	row.append(link);

	if (config.loggedIn && item.karta) {
		const cart = document.createElement('a');
		cart.className = 'btn btn-light btn-sm ajax cart-add autocomplete-result__cart loading-action loading-action--icon';
		cart.href = cartDialogUrl(item.karta);
		cart.rel = 'nofollow';
		cart.setAttribute('aria-label', `${messages.cart || ''}: ${item.label || item.karta}`);
		cart.title = messages.cart || '';
		cart.append(createIcon('cart-plus'));
		cart.addEventListener('click', (event) => requestMultiCartDialog(cart, event));
		row.append(cart);
	}
	return row;
}

function createCategory(item, term) {
	const link = document.createElement('a');
	link.className = 'autocomplete-result autocomplete-result--category autocomplete-primary-action';
	link.href = suggestionUrl(item.url, term);
	link.append(createIcon('grid-3x3-gap-fill', 'autocomplete-result__category-icon'));
	const label = document.createElement('span');
	label.className = 'autocomplete-result__title';
	label.append(createHighlightedContent(item.h, item.label));
	link.append(label, createIcon('chevron-right', 'autocomplete-result__arrow'));
	return link;
}

function createSection(title, className, items) {
	const section = document.createElement('section');
	section.className = `autocomplete-section ${className}`;
	const heading = document.createElement('h2');
	heading.className = 'autocomplete-section__title';
	heading.textContent = title;
	const results = document.createElement('div');
	results.className = 'autocomplete-section__results';
	results.append(...items);
	section.append(heading, results);
	return section;
}

function createState(icon, message, className = '') {
	const state = document.createElement('div');
	state.className = `autocomplete-state${className ? ` ${className}` : ''}`;
	state.append(createIcon(icon), document.createTextNode(message));
	return state;
}

function createSkeleton() {
	const skeleton = document.createElement('div');
	skeleton.className = 'autocomplete-skeleton';
	for (let index = 0; index < 3; index += 1) {
		const row = document.createElement('span');
		row.className = 'autocomplete-skeleton__row';
		row.append(document.createElement('span'), document.createElement('span'));
		skeleton.append(row);
	}
	return skeleton;
}

function bind(input) {
	if (!input || !once(input, 'autocomplete')) {
		return;
	}
	const config = getConfig();
	const messages = config.autocomplete || {};
	const panel = document.createElement('div');
	panel.className = 'autocomplete-items';
	panel.id = `${input.id}-dialog`;
	panel.setAttribute('role', 'dialog');
	panel.setAttribute('aria-label', messages.results || '');
	panel.hidden = true;
	const status = document.createElement('span');
	status.className = 'sr-only';
	status.setAttribute('aria-live', 'polite');
	status.setAttribute('aria-atomic', 'true');
	input.setAttribute('role', 'combobox');
	input.setAttribute('aria-autocomplete', 'list');
	input.setAttribute('aria-haspopup', 'dialog');
	input.setAttribute('aria-controls', panel.id);
	input.setAttribute('aria-expanded', 'false');
	input.parentElement.append(panel, status);

	let controller;
	let requestVersion = 0;
	let restoringFocus = false;
	const open = () => {
		panel.hidden = false;
		input.setAttribute('aria-expanded', 'true');
	};
	const close = (restoreFocus = false) => {
		requestVersion += 1;
		controller?.abort();
		panel.hidden = true;
		input.setAttribute('aria-expanded', 'false');
		if (restoreFocus) {
			restoringFocus = true;
			input.focus();
			window.setTimeout(() => {
				restoringFocus = false;
			}, 0);
		}
	};
	const showLoading = () => {
		panel.replaceChildren(createSkeleton());
		panel.setAttribute('aria-busy', 'true');
		status.textContent = messages.loading || '';
		open();
	};
	const showError = () => {
		panel.removeAttribute('aria-busy');
		panel.replaceChildren(createState('exclamation-circle', messages.error || '', 'autocomplete-state--error'));
		status.textContent = messages.error || '';
		open();
	};
	const render = (items, term, recent = false) => {
		const products = items.filter((item) => item.cat !== 'catK'
			&& (!recent || Number(item.karta) !== Number(messages.currentProductId)))
			.slice(0, recent ? 10 : PRODUCT_LIMIT);
		const categories = items.filter((item) => item.cat === 'catK'
			&& (!recent || item.url !== messages.currentCategoryUrl))
			.slice(0, recent ? 10 : CATEGORY_LIMIT);
		panel.removeAttribute('aria-busy');
		panel.replaceChildren();

		if (!products.length && !categories.length) {
			if (recent) {
				status.textContent = '';
				close();
				return;
			}
			const empty = interpolate(messages.empty, {query: term});
			panel.append(createState('search', empty));
			status.textContent = empty;
			open();
			return;
		}

		const layout = document.createElement('div');
		layout.className = 'autocomplete-layout';
		if (!products.length || !categories.length) {
			layout.classList.add('autocomplete-layout--single');
		}
		if (products.length) {
			layout.append(createSection(
				(recent ? messages.recentProducts : messages.products) || '',
				'autocomplete-section--products',
				products.map((item) => createProduct(item, term, config)),
			));
		}
		if (categories.length) {
			layout.append(createSection(
				(recent ? messages.recentCategories : messages.categories) || '',
				'autocomplete-section--categories',
				categories.map((item) => createCategory(item, term)),
			));
		}

		const allResults = document.createElement('a');
		allResults.className = 'autocomplete-all-results autocomplete-primary-action';
		allResults.href = `${config.langUrl || '/'}search?q=${encodeURIComponent(term)}`;
		allResults.append(
			document.createTextNode(interpolate(messages.allResults, {query: term})),
			createIcon('arrow-right'),
		);
		panel.append(layout);
		if (!recent) {
			panel.append(allResults);
		}
		// The history is not a search result, so it must not be announced as one.
		status.textContent = recent
			? [products.length ? messages.recentProducts : '', categories.length ? messages.recentCategories : '']
				.filter(Boolean).join(', ')
			: interpolate(messages.found, {
				products: products.length,
				categories: categories.length,
			});
		open();
	};

	const request = debounce(async (version, term) => {
		if (version !== requestVersion) {
			return;
		}
		controller = new AbortController();
		const recent = term.length === 0;
		const path = recent ? 'ajax/recent-catalog-visits' : `ajax-autocomplete?term=${encodeURIComponent(term)}`;
		try {
			const response = await fetch(`${config.langUrl || '/'}${path}`, {
				signal: controller.signal,
				cache: 'no-store',
				headers: {'X-Requested-With': 'XMLHttpRequest'},
			});
			if (!response.ok) {
				throw new Error(`Autocomplete failed with status ${response.status}.`);
			}
			const data = await response.json();
			if (version !== requestVersion || input.value.trim() !== term) {
				return;
			}
			const items = recent ? [...(data.products || []), ...(data.categories || [])] : data;
			render(Array.isArray(items) ? items : [], term, recent);
		} catch (error) {
			if (error.name !== 'AbortError' && version === requestVersion) {
				if (recent) {
					close();
				} else {
					showError();
				}
			}
		}
	});

	const update = () => {
		controller?.abort();
		const version = ++requestVersion;
		const term = input.value.trim();
		if (term.length === 1) {
			close();
			return;
		}
		if (term.length >= 2) {
			showLoading();
		} else {
			panel.hidden = true;
			input.setAttribute('aria-expanded', 'false');
		}
		request(version, term);
	};
	input.addEventListener('input', update);
	input.addEventListener('click', () => {
		if (panel.hidden) {
			update();
		}
	});
	input.addEventListener('focus', () => {
		if (!restoringFocus) {
			update();
		}
	});
	input.addEventListener('keydown', (event) => {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			const actions = panel.querySelectorAll('.autocomplete-primary-action');
			const first = event.key === 'ArrowDown' ? actions[0] : actions[actions.length - 1];
			if (first && !panel.hidden) {
				event.preventDefault();
				first.focus();
			}
		} else if (event.key === 'Escape') {
			close();
		}
	});
	panel.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			event.preventDefault();
			close(true);
			return;
		}
		if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
			return;
		}
		const actions = [...panel.querySelectorAll('.autocomplete-primary-action')];
		const index = actions.indexOf(document.activeElement);
		if (index < 0 || !actions.length) {
			return;
		}
		event.preventDefault();
		if (event.key === 'Home') {
			actions[0].focus();
		} else if (event.key === 'End') {
			actions[actions.length - 1].focus();
		} else {
			const offset = event.key === 'ArrowDown' ? 1 : -1;
			actions[(index + offset + actions.length) % actions.length].focus();
		}
	});
	document.addEventListener('focusin', (event) => {
		if (document.body.classList.contains('modal-open')) {
			return;
		}
		if (event.target !== input && !panel.contains(event.target)) {
			close();
		}
	});
	document.addEventListener('click', (event) => {
		if (document.body.classList.contains('modal-open')) {
			return;
		}
		if (!panel.contains(event.target) && event.target !== input) {
			close();
		}
	});
}

export function init(root = document) {
	bind(root.querySelector('#frm-searchForm-q'));
}
